/*!
 * totalElement magnet polarity badges  (assets/te-polarity.js)
 *
 * Draws a small magnet at the bottom-left of product card images, in the same style as
 * snippets/magnet-polarity-drawing.liquid (CSS magnet, red north, gray south,
 * countersinks from cs_hole_size; the N and S letters are off on cards, see cardLetters).
 * It only appears for products whose "Magnetization Direction" metafield
 * (TeExtMf1-Prod.magnetization_direction) has a value.
 *
 * Grade and pull force: the "Grade" metafield (TeExtMf1-Prod.grade, e.g. N52) and the
 * "Pull Force" metafield (TeExtMf1-Prod.pull_force) are shown as a white grade tag and
 * the store's dark pull-force tag: on product cards stacked in the bottom-right corner
 * (grade on top), on the product page stacked in the top-right corner (pull force on top,
 * moved down below the theme's SALE tag if the two would overlap).
 * Products with no direction (hooks, sewing, fishing and threaded pot
 * magnets) get the tags on their own. Where the pull-force tag shows, the theme's older tag
 * (.product-pull-force-image from totalelement-pull-force-init.js, and the product
 * page's top-left tag) is hidden so the value never appears twice.
 *
 *   - Shape comes from the listing's Size metafield, exactly as the product-page
 *     drawing reads it: 1 number = sphere, 2 = disc/cylinder, 3 with "Inner Diameter" =
 *     ring, 3 with Width/Length = block. Size (decimal) is used if Size is empty.
 *   - Countersunk discs and blocks use cs_hole_size. "Pair" (or no North/South) shows
 *     two magnets: countersunk on the north side and on the south side.
 *   - Anything thinner or longer than 3:1 is drawn at 3:1 so it stays readable on cards.
 *
 * Where the data comes from
 *   Product page:   <script data-te-polarity="product"> written by snippets/te-polarity.liquid
 *   Product cards:  /collections/all?view=te-polarity (templates/collection.te-polarity.liquid),
 *                   cached in the browser for a few hours. Works for Boost-rendered
 *                   cards, Liquid cards, search results and recommendation widgets.
 *
 * The drawing engine below is a copy of the one inside magnet-polarity-drawing.liquid,
 * so badges match the product-page drawing. If you change that engine, paste the new
 * version over the "DRAWING ENGINE" section here too.
 *
 * Optional settings: define window.tePolarityConfig = { ... } before this file loads.
 */
(function (w, d) {
  'use strict';
  if (w.TEPolarity) return;

  /* ------------------------------------------------------------------ settings */
  var CFG = {
    maxRatio: 3,                     // longest side : shortest side never drawn beyond this
    dataUrl: '/collections/all?view=te-polarity',
    cacheKey: 'tePolarity.v4',       // change this to force every browser to refetch
    cacheHours: 6,
    learnUrl: '/blogs/about-neodymium-magnets/neodymium-magnetization-direction',
    linkOnProductPage: true,         // product-page badge links to the FAQ page
    minImageWidth: 90,               // skip tiny thumbnails (cart, search dropdown)
    galleryImages: '.product-photo-container .product__image-wrapper',
    cardLinks: 'a[href*="/products/"]',
    imageHosts: '.product__image-wrapper, .lazyload__image-wrapper > div, .product-list-image, .product-grid-image--centered',
    exclude: 'header, .site-header, #CartDrawer, .ajaxcart, .cart-row, .boost-pfs-search-suggestion-wrapper, ' +
             '.boost-pfs-search-suggestion, .product-photo-thumbs, .te-mag-wrap, [data-te-pol-skip]',
    data: null,                      // optional preloaded { handle: product } map
    renderSize: 200,                 // drawn at this size (like the gallery thumbnails), then scaled down
    pairGap: 24,
    // What to show. Any setting here can also be changed from theme.liquid without editing
    // this file: put  window.tePolarityConfig = { polarity: false };  in a script tag
    // above the line that loads te-polarity.js.
    polarity: false,                 // magnet diagram: true = everywhere, false = hidden,
                                     // 'cards' = product cards only, 'product' = product page only
    cardLetters: false,              // N and S on the magnet in product cards: true = show, false = hide
    // Grade and pull force tags
    grade: true,                     // false = no grade tag
    gradeLabel: 'Grade',             // shown before the grade on the product page ('' for none)
    pullForce: true,                 // false = no pull-force tag
    pullForceLabel: 'Pull Force',    // product page: shown after the value, e.g. "7.9 lbs Pull Force" ('' for none)
    // Corners are set in te-polarity.css (.te-pol-tags and .te-pol-mag; cards: .te-pol--card)
    pullForceUrl: '',                // optional page the product-page tag links to
    hideOldPullForce: true,          // hide the theme's older pull-force tag where this one shows
    fontPx: [12, 19],                // product-page tag text size range in px; scales with the image width
                                     // (card tags are a fixed 14px, set in te-polarity.css)
    // Same settings names and defaults as magnet-polarity-drawing.liquid
    // (finish 'satin' matches its small gallery thumbnails; use 'glossy' for the big-drawing look)
    drawing: {
      north: '#e0212c', south: '#80848b', axialNorth: 'top', diametricNorth: 'left',
      tilt: 0.32, csTilt: 0.5, edgeRadius: '', finish: 'satin', glossIntensity: 1, glossColor: '#ffffff',
      bg: '#ffffff', letters: true,
      letterScale: 1.5               // N and S 1.5x larger than on the product-page drawing (when shown)
    }
  };
  var userCfg = w.tePolarityConfig || {};
  Object.keys(userCfg).forEach(function (k) {
    if (k === 'drawing') Object.keys(userCfg.drawing).forEach(function (c) { CFG.drawing[c] = userCfg.drawing[c]; });
    else CFG[k] = userCfg[k];
  });
  // 'false' and 'true' typed with quotes work the same as false and true
  ['polarity', 'cardLetters', 'grade', 'pullForce'].forEach(function (k) {
    if (CFG[k] === 'false') CFG[k] = false; else if (CFG[k] === 'true') CFG[k] = true;
  });

  /* ================================================================== DRAWING ENGINE
     Copied from snippets/magnet-polarity-drawing.liquid (var TeMag = ...). Kept private
     to this file so it never replaces the product page's own window.TeMag.
     Only change: the three lines marked "te-polarity: letterScale" (bigger N/S on badges). */
  var TeMag = (function(){
    function r(n){ return Math.round(n*100)/100; }
    function trimNum(n,d){ return String(+(+n).toFixed(d)); }
    function esc(s){ return String(s).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;'); }

    function parseNum(s){
      var m; s = String(s).trim();
      if ((m = s.match(/^(\d+)[\s-]+(\d+)\s*\/\s*(\d+)$/))) return +m[1] + m[2]/m[3];
      if ((m = s.match(/^(\d+)\s*\/\s*(\d+)$/))) return m[1]/m[2];
      if (/^\d*\.?\d+$/.test(s)) return parseFloat(s);
      return NaN;
    }
    function normRaw(s){ return String(s).trim().replace(/\s*\/\s*/g,'/').replace(/[\s-]+(?=\d+\/)/,' '); }

    var DEFAULT_ROLES = { sphere:['d'], disc:['d','t'], ring:['d','id','t'], block:['w','l','t'] };

    function roleOf(word){
      word = word.toLowerCase();
      if (/inner|\bi\.?d\b/.test(word)) return 'id';
      if (/width/.test(word)) return 'w';
      if (/length/.test(word)) return 'l';
      if (/thick|height/.test(word)) return 't';
      if (/outer|\bo\.?d\b|diam/.test(word)) return 'd';
      return null;
    }

    /* Direction text -> 'axial' | 'diametric' | null */
    function parseDir(raw){
      var s = String(raw || '').toLowerCase();
      if (/diamet/.test(s)) return 'diametric';
      if (/axial|thick|through/.test(s)) return 'axial';
      return null;
    }

    /* Size text (+ direction text) -> spec, or { error } */
    function parseSize(raw, dirRaw, defUnit){
      var low = String(raw || '').toLowerCase().replace(/[”″]/g,'"');
      var pm = low.match(/\(([^)]*)\)/), paren = pm ? pm[1] : '';
      var body = low.replace(/\([^)]*\)/g,' ');
      var unit = /mm|millimet/.test(body) ? 'mm' : (/inch|\bin\b|"/.test(body) ? 'in' : (defUnit === 'mm' ? 'mm' : 'in'));
      body = body.replace(/millimeters?|millimetres?|mm|inches|inch|\bin\b\.?|"|diameter|\bdia\b\.?|thickness|thick|ø|⌀/g, ' ');
      var parts = body.split(/\s*(?:×|\*|\bby\b|x)\s*/).map(function(p){ return p.trim(); }).filter(Boolean);
      if (parts.length < 1 || parts.length > 3) return { error:'Size needs 1 to 3 numbers.' };
      var vals = parts.map(parseNum);
      for (var i = 0; i < vals.length; i++) if (!(vals[i] > 0)) return { error:'"' + parts[i] + '" is not a size above zero.' };

      var roles = paren ? paren.split(/\s+(?:x|×|by)\s+|\s*[×*]\s*/).map(roleOf) : [];
      var shape;
      if (vals.length === 1) shape = 'sphere';
      else if (vals.length === 2) shape = 'disc';
      else if (roles.indexOf('id') > -1) shape = 'ring';
      else if (roles.indexOf('w') > -1 || roles.indexOf('l') > -1) shape = 'block';
      else shape = /thick/.test(String(dirRaw || '').toLowerCase()) ? 'block' : 'ring';

      var def = DEFAULT_ROLES[shape];
      var ok = roles.length === vals.length && roles.every(function(x){ return x && def.indexOf(x) > -1; }) &&
               def.every(function(x){ return roles.indexOf(x) > -1; });
      if (!ok) roles = def;

      var spec = { shape:shape, unit:unit, v:{}, raw:{} };
      roles.forEach(function(role, i){ spec.v[role] = vals[i]; spec.raw[role] = normRaw(parts[i]); });
      if (shape === 'ring' && !(spec.v.id < spec.v.d)) return { error:'Inner diameter must be smaller than the outer diameter.' };

      var d = String(dirRaw || '').toLowerCase();
      if (shape === 'block') spec.mag = (!d || /thick|axial|through/.test(d)) ? 'axial' : null;
      else spec.mag = d ? parseDir(d) : 'axial';
      if (!spec.mag) return { error:'Magnetization direction "' + dirRaw + '" is not supported for this shape.' };
      return spec;
    }

    /* Countersink text, e.g. "0.295 x 0.17 Inches at 82°" -> { D, H, angle, rawD, rawH, unit } */
    function parseCs(raw, defUnit){
      if (raw == null || !String(raw).trim()) return null;
      var low = String(raw).toLowerCase().replace(/[”″]/g,'"').replace(/\([^)]*\)/g,' ');
      /* two holes: "... - 1 Inches Apart" (center-to-center spacing) */
      var spacing = null;
      var sm = low.match(/(\d+(?:\.\d+)?(?:[\s-]+\d+\/\d+|\/\d+)?)\s*(millimeters?|millimetres?|mm|inches|inch|in\b\.?|")?\s*(?:apart|c-c|c\/c|center[\s-]+to[\s-]+center|on[\s-]+center)/);
      if (sm){
        spacing = { v:parseNum(sm[1].trim()), raw:normRaw(sm[1]), unit:sm[2] ? (/^m/.test(sm[2]) ? 'mm' : 'in') : null };
        low = low.replace(sm[0], ' ');
        if (!(spacing.v > 0)) spacing = null;
      }
      var am = low.match(/(?:\bat\b|@)\s*(\d+(?:\.\d+)?)/) || low.match(/(\d+(?:\.\d+)?)\s*(?:°|º|deg)/);
      var angle = am ? +am[1] : 82;
      var body = low.replace(/(?:\bat\b|@)\s*\d+(?:\.\d+)?\s*(?:°|º|degrees?|deg)?/g,' ')
                    .replace(/\d+(?:\.\d+)?\s*(?:°|º|degrees?|deg)/g,' ');
      var unit = /mm|millimet/.test(body) ? 'mm' : (/inch|\bin\b|"/.test(body) ? 'in' : (defUnit === 'mm' ? 'mm' : 'in'));
      body = body.replace(/millimeters?|millimetres?|mm|inches|inch|\bin\b\.?|"|diameter|\bdia\b\.?|countersunk|countersink|\bcs\b|\bhole\b/g,' ');
      var parts = body.split(/\s*(?:×|\*|\bby\b|x)\s*/).map(function(p){ return p.replace(/^[\s\-–—,;:]+|[\s\-–—,;:]+$/g, ''); }).filter(Boolean);
      if (parts.length !== 2) return { error:'Countersink needs "countersink diameter x hole diameter".' };
      var D = parseNum(parts[0]), H = parseNum(parts[1]);
      if (!(D > 0) || !(H > 0)) return { error:'Countersink sizes must be above zero.' };
      if (H > D){ var t = D; D = H; H = t; t = parts[0]; parts[0] = parts[1]; parts[1] = t; }
      if (!(angle > 0 && angle < 180)) angle = 82;
      if (spacing && !spacing.unit) spacing.unit = unit;
      return { D:D, H:H, angle:angle, rawD:normRaw(parts[0]), rawH:normRaw(parts[1]), unit:unit, spacing:spacing };
    }

    /* Which countersunk faces to draw. Returns one settings override per image. */
    function csVariants(spec, dirRaw){
      if (!spec.cs || spec.mag !== 'axial') return [{}];
      var d = String(dirRaw || '').toLowerCase();
      var pair = /\bpairs?\b/.test(d), n = /\bnorth\b/.test(d), s = /\bsouth\b/.test(d);
      var NV = { axialNorth:'top', caption:'Countersunk on North side' };
      var SV = { axialNorth:'bottom', caption:'Countersunk on South side' };
      if (pair || (n && s) || (!n && !s)) return [NV, SV];
      return n ? [NV] : [SV];
    }

    /* Attach a countersink to a parsed size spec (discs and blocks only). */
    function addCs(spec, csRaw){
      if (!spec || spec.error || (spec.shape !== 'disc' && spec.shape !== 'block')) return spec;
      var cs = parseCs(csRaw, spec.unit);
      if (!cs) return spec;
      if (cs.error){ spec.csError = cs.error; return spec; }
      var f = cs.unit === spec.unit ? 1 : (cs.unit === 'mm' ? 1/25.4 : 25.4);
      cs.Dm = cs.D * f; cs.Hm = cs.H * f;
      cs.depth = (cs.Dm - cs.Hm) / 2 / Math.tan(cs.angle * Math.PI / 360);
      if (cs.spacing && spec.shape === 'block'){
        var fs2 = cs.spacing.unit === spec.unit ? 1 : (cs.spacing.unit === 'mm' ? 1/25.4 : 25.4);
        cs.Sm = cs.spacing.v * fs2;                    /* hole spacing, in the magnet's unit */
      }
      spec.cs = cs;
      return spec;
    }

    /* A length like "0.3 mm", "0.01 in", "R0.5" or 0.02 -> value in the magnet's unit (0 = none) */
    function parseLen(raw, unit){
      if (raw == null) return 0;
      var s = String(raw).toLowerCase().replace(/[”″]/g,'"').trim();
      if (!s) return 0;
      var u = /mm|millimet/.test(s) ? 'mm' : (/inch|\bin\b|"/.test(s) ? 'in' : unit);
      var n = parseNum(s.replace(/^r\s*/, '').replace(/millimeters?|millimetres?|mm|inches|inch|\bin\b\.?|"/g, '').trim());
      if (!(n > 0)) return 0;
      return u === unit ? n : (u === 'mm' ? n/25.4 : n*25.4);
    }

    function hexRgb(c){
      var s = String(c || '').trim().toLowerCase(), m;
      if (s === 'white') return [255, 255, 255];
      if ((m = s.match(/^#?([0-9a-f]{3})$/))) return m[1].split('').map(function(x){ return parseInt(x + x, 16); });
      if ((m = s.match(/^#?([0-9a-f]{6})$/))) return [0, 2, 4].map(function(i){ return parseInt(m[1].substr(i, 2), 16); });
      if ((m = s.match(/^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/))) return [+m[1], +m[2], +m[3]];
      return null;
    }

    function label(raw, v, unit, conv, inch){
      var t;
      if (unit === 'in'){
        t = (String(raw).indexOf('/') > -1 || /^\d*\.?\d+$/.test(String(raw)) ? raw : trimNum(v,4)) + inch;
        if (conv) t += ' (' + trimNum(v*25.4,2) + ' mm)';
      } else {
        t = (/^\d*\.?\d+$/.test(String(raw)) ? raw : trimNum(v,3)) + ' mm';
        if (conv) t += ' (' + trimNum(v/25.4,3) + inch + ')';
      }
      return t;
    }

    var DEFAULTS = { north:'#e0212c', south:'#80848b', axialNorth:'top', diametricNorth:'left', max:200, tilt:0.32,
                     dims:true, letters:true, phi:true, conv:false, dimColor:'#3a3f47', fs:14, inch:'"', bg:'#ffffff', finish:'glossy', caption:'', csTilt:0.5, csBlockTurn:24, edgeRadius:'', glossIntensity:1, glossColor:'#ffffff' };

    /* spec + settings -> { html, width, height } using only inline styles */
    function build(spec, opts){
      var cfg = {}, k;
      for (k in DEFAULTS) cfg[k] = DEFAULTS[k];
      for (k in opts || {}) if (opts[k] !== undefined && opts[k] !== null && opts[k] !== '') cfg[k] = opts[k];
      var N = cfg.north, S = cfg.south, c = cfg.dimColor, fs = +cfg.fs, tilt = +cfg.tilt, maxPx = +cfg.max;
      if (spec.cs && +cfg.csTilt > tilt) tilt = +cfg.csTilt;          /* countersunk: view from higher so the hole shows */
      var v = spec.v, unit = spec.unit;
      var phi = cfg.phi ? 'Ø ' : '';
      var L = function(key){ return label(spec.raw[key], v[key], unit, cfg.conv, cfg.inch); };

      var out = [], bx = { x1:0, y1:0, x2:0, y2:0 };
      function ext(x1,y1,x2,y2){ bx.x1 = Math.min(bx.x1,x1,x2); bx.y1 = Math.min(bx.y1,y1,y2); bx.x2 = Math.max(bx.x2,x1,x2); bx.y2 = Math.max(bx.y2,y1,y2); }
      function css(o){ var a = []; for (var p in o) a.push(p + ':' + o[p]); return a.join(';'); }
      function node(o, text, inner){
        var tag = text != null ? 'span' : 'div';
        return '<' + tag + ' style="' + esc('position:absolute;' + css(o)) + '">' + (text != null ? esc(text) : (inner || '')) + '</' + tag + '>';
      }
      function add(o, text){ out.push(node(o, text)); }
      function px(n){ return r(n) + 'px'; }
      function textW(t){ return t.length * fs * 0.58; }
      var TH = fs * 1.25;

      var GLOSS = cfg.finish !== 'satin';
      /* side of a round magnet: dark edges, a sharp specular streak, and a softer second reflection */
      var SH = GLOSS
        ? 'linear-gradient(90deg,rgba(0,0,0,.48) 0%,rgba(0,0,0,.18) 7%,rgba(255,255,255,.10) 14%,rgba(255,255,255,.78) 21%,rgba(255,255,255,.22) 26%,rgba(255,255,255,0) 36%,rgba(0,0,0,.06) 55%,rgba(255,255,255,.26) 71%,rgba(255,255,255,.04) 78%,rgba(0,0,0,.22) 90%,rgba(0,0,0,.52) 100%)'
        : 'linear-gradient(90deg,rgba(0,0,0,.30) 0%,rgba(255,255,255,.20) 22%,rgba(255,255,255,.06) 45%,rgba(0,0,0,.10) 72%,rgba(0,0,0,.34) 100%)';
      /* top face: hot spot plus a diagonal sheen */
      var HL = GLOSS
        ? 'radial-gradient(ellipse 30% 45% at 30% 38%,rgba(255,255,255,.70),rgba(255,255,255,0) 100%),linear-gradient(165deg,rgba(255,255,255,.42) 0%,rgba(255,255,255,.10) 38%,rgba(255,255,255,0) 52%,rgba(0,0,0,.14) 100%)'
        : 'radial-gradient(ellipse at 38% 40%,rgba(255,255,255,.28),rgba(255,255,255,0) 62%)';
      var RIM = GLOSS ? 'inset 0 1px 1px rgba(255,255,255,.65),inset 0 0 0 1px rgba(0,0,0,.12)' : 'inset 0 0 0 1px rgba(0,0,0,.08)';

      var axial = spec.mag === 'axial';
      var nFirst = axial ? cfg.axialNorth !== 'bottom' : cfg.diametricNorth !== 'right';
      var A = nFirst ? N : S, B = nFirst ? S : N;            /* A = top or left color */
      var aL = nFirst ? 'N' : 'S', bL = nFirst ? 'S' : 'N';
      var split = 'linear-gradient(90deg,' + A + ' 50%,' + B + ' 50%)';
      var where = axial ? (spec.shape === 'block' ? 'magnetized through thickness, ' : 'axially magnetized, ') + (nFirst ? 'north pole on top' : 'north pole on bottom')
                        : 'diametrically magnetized, ' + (nFirst ? 'north pole on left half' : 'north pole on right half');

      /* ---------- dimension helpers ---------- */
      function arrow(dir, x, y){
        var a = 8, b = 3.5, tb = b + 'px solid transparent', s = { width:'0', height:'0' };
        if (dir === 'left')  { s.left = px(x);   s.top = px(y-b); s['border-top'] = tb; s['border-bottom'] = tb; s['border-right'] = a + 'px solid ' + c; }
        if (dir === 'right') { s.left = px(x-a); s.top = px(y-b); s['border-top'] = tb; s['border-bottom'] = tb; s['border-left'] = a + 'px solid ' + c; }
        if (dir === 'up')    { s.left = px(x-b); s.top = px(y);   s['border-left'] = tb; s['border-right'] = tb; s['border-bottom'] = a + 'px solid ' + c; }
        if (dir === 'down')  { s.left = px(x-b); s.top = px(y-a); s['border-left'] = tb; s['border-right'] = tb; s['border-top'] = a + 'px solid ' + c; }
        return node(s);
      }
      function textNode(x, y, t, tf){ return node({ left:px(x), top:px(y), transform:tf, 'white-space':'nowrap', 'line-height':'1.2' }, t); }

      /* horizontal dimension from x1..x2 at y; extension lines start at fromY; label below (or above) */
      function hDim(x1, x2, y, fromY, t, above){
        var len = x2 - x1;
        var top = above ? y - 5 : fromY, hgt = above ? fromY - (y - 5) : y + 5 - fromY;
        out.push(node({ left:px(x1), top:px(top), width:px(len), height:px(hgt), 'border-left':'1px solid ' + c, 'border-right':'1px solid ' + c, opacity:'.6' }));
        out.push(node({ left:px(x1), top:px(y), width:px(len), height:'1px', background:c }));
        if (len >= 26){ out.push(arrow('left', x1, y+0.5)); out.push(arrow('right', x2, y+0.5)); }
        else { out.push(arrow('right', x1, y+0.5)); out.push(arrow('left', x2, y+0.5)); }
        var ly = above ? y - 6 - TH : y + 6, w = textW(t);
        out.push(textNode((x1+x2)/2, ly, t, 'translateX(-50%)'));
        ext((x1+x2)/2 - w/2, ly, (x1+x2)/2 + w/2, ly + TH);
        ext(x1 - (len < 26 ? 9 : 0), top, x2 + (len < 26 ? 9 : 0), top + hgt);
      }
      /* vertical dimension at x from y1..y2; extension lines from fromX; label to the right */
      function vDim(x, y1, y2, fromX, t){
        var len = y2 - y1;
        out.push(node({ left:px(fromX+3), top:px(y1), width:px(x - fromX - 1), height:px(len), 'border-top':'1px solid ' + c, 'border-bottom':'1px solid ' + c, opacity:'.6' }));
        out.push(node({ left:px(x), top:px(y1), width:'1px', height:px(len), background:c }));
        if (len >= 26){ out.push(arrow('up', x+0.5, y1)); out.push(arrow('down', x+0.5, y2)); }
        else { out.push(arrow('down', x+0.5, y1)); out.push(arrow('up', x+0.5, y2)); }
        out.push(textNode(x + 9, (y1+y2)/2, t, 'translateY(-50%)'));
        ext(fromX, y1 - (len < 26 ? 9 : 0), x + 9 + textW(t), y2 + (len < 26 ? 9 : 0));
        ext(x, (y1+y2)/2 - TH/2, x, (y1+y2)/2 + TH/2);
      }
      /* dimension parallel to a slanted edge (x1,y1)->(x2,y2), offset to the right-hand side */
      function sDim(x1, y1, x2, y2, t, off){
        /* off > 0: dimension on the right-hand side of x1->x2 (label beyond the line);
           off < 0: on the left-hand side (label between the line and the part) */
        var dx = x2 - x1, dy = y2 - y1, len = Math.sqrt(dx*dx + dy*dy), ang = Math.atan2(dy, dx);
        var inner = [], neg = off < 0;
        var eTop = neg ? off - 2 : 3, eH = neg ? -off - 1 : off + 2;
        inner.push(node({ left:'0', top:px(eTop), width:px(len), height:px(eH), 'border-left':'1px solid ' + c, 'border-right':'1px solid ' + c, opacity:'.6' }));
        inner.push(node({ left:'0', top:px(off), width:px(len), height:'1px', background:c }));
        if (len >= 26){ inner.push(arrow('left', 0, off+0.5)); inner.push(arrow('right', len, off+0.5)); }
        else { inner.push(arrow('right', 0, off+0.5)); inner.push(arrow('left', len, off+0.5)); }
        var ty = neg ? off + 5 : off + 5;
        inner.push(textNode(len/2, ty, t, 'translateX(-50%)'));
        out.push(node({ left:px(x1), top:px(y1), width:'0', height:'0', transform:'rotate(' + r(ang*180/Math.PI) + 'deg)', 'transform-origin':'0 0' }, null, inner.join('')));
        var ca = Math.cos(ang), sa = Math.sin(ang), tw = textW(t);
        [[0,0],[len,0],[len/2 - tw/2, ty],[len/2 + tw/2, ty + TH],[-9,off],[len+9,off],[0,eTop],[len,eTop]].forEach(function(p){
          var gx = x1 + p[0]*ca - p[1]*sa, gy = y1 + p[0]*sa + p[1]*ca; ext(gx, gy, gx, gy);
        });
      }

      /* ---------- pole letters ---------- */
      var pf;
      function pole(x, y, t, size){ out.push(node({ left:px(x), top:px(y), transform:'translate(-50%,-50%)', 'font-size':px(size || pf), 'font-weight':'700', 'line-height':'1', color:'#fff', 'text-shadow':'0 1px 2px rgba(0,0,0,.4)' }, t)); }
      /* N and S are always drawn as a pair at one size: pick the first placement that is
         nearly as large as the best one; if nothing fits at 9px, show neither. */
      /* Axial letters: the top-pole letter sits directly above the bottom-pole letter,
         each centered in its half of the side, at the same size. If the bottom letter
         doesn't fit its half (under 9px), neither is drawn. */
      /* ---------- edge radius ---------- */
      /* "8%" = percent of the magnet's smallest dimension (usually the thickness; 50% = fully round),
         otherwise a real length ("0.3 mm", "0.01 in", or a plain number in the size's unit) */
      var erUnits = 0, sc, erRaw = String(cfg.edgeRadius == null ? '' : cfg.edgeRadius).trim();
      if (/%\s*$/.test(erRaw)){
        var pct = parseFloat(erRaw), vv = spec.v;
        var smallest = spec.shape === 'block' ? Math.min(vv.w, vv.l, vv.t)
                     : spec.shape === 'ring' ? Math.min(vv.t, (vv.d - vv.id)/2)
                     : Math.min(vv.d || Infinity, vv.t || Infinity);
        if (pct > 0 && isFinite(smallest)) erUnits = smallest * Math.min(pct, 50) / 100;
      } else erUnits = parseLen(erRaw, spec.unit);
      function edgePx(){ return erUnits > 0 && sc ? erUnits * sc : 0; }

      /* convex polygon with rounded corners (fillets approximated by curves) */
      function roundPoly(pts, rr){
        var outP = [], n = pts.length;
        for (var i = 0; i < n; i++){
          var P = pts[i], Q = pts[(i - 1 + n) % n], S2 = pts[(i + 1) % n];
          var ax = Q[0]-P[0], ay = Q[1]-P[1], bx = S2[0]-P[0], by = S2[1]-P[1];
          var la = Math.sqrt(ax*ax + ay*ay), lb = Math.sqrt(bx*bx + by*by);
          if (la < 0.01 || lb < 0.01){ outP.push(P); continue; }
          ax /= la; ay /= la; bx /= lb; by /= lb;
          var ang = Math.acos(Math.max(-1, Math.min(1, ax*bx + ay*by)));
          var d = Math.min(rr / Math.tan(ang/2), la/2, lb/2);
          var p0 = [P[0] + ax*d, P[1] + ay*d], p2 = [P[0] + bx*d, P[1] + by*d];
          for (var k = 0; k <= 6; k++){
            var u = k/6, m = 1 - u;
            outP.push([m*m*p0[0] + 2*m*u*P[0] + u*u*p2[0], m*m*p0[1] + 2*m*u*P[1] + u*u*p2[1]]);
          }
        }
        return outP;
      }
      /* soft band along an edge: 'center' = highlight straddling an inside edge,
         otherwise a band on the side toward (cx,cy) that fades away from the edge */
      function edgeBand(p1, p2, wid, color, center, cxy){
        var dx = p2[0]-p1[0], dy = p2[1]-p1[1], len = Math.sqrt(dx*dx + dy*dy);
        if (len < 1) return '';
        var ang = Math.atan2(dy, dx), nx = -Math.sin(ang), ny = Math.cos(ang), bg, top;
        if (center){ top = -wid; bg = 'linear-gradient(180deg,rgba(0,0,0,0),' + color + ' 50%,rgba(0,0,0,0))'; wid = wid*2; }
        else if ((cxy[0]-p1[0])*nx + (cxy[1]-p1[1])*ny >= 0){ top = 0; bg = 'linear-gradient(180deg,' + color + ',rgba(0,0,0,0))'; }
        else { top = -wid; bg = 'linear-gradient(0deg,' + color + ',rgba(0,0,0,0))'; }
        return node({ left:px(p1[0]), top:px(p1[1]), width:px(len), height:px(wid), transform:'rotate(' + r(ang*180/Math.PI) + 'deg) translateY(' + r(top) + 'px)', 'transform-origin':'0 0', background:bg });
      }
      /* wrap everything drawn since index `from` in a container clipped to the rounded outline,
         then add edge bands: inner = [[p1,p2],...] highlights, outer = silhouette edges */
      function roundBlock(from, BW, BH, sil, inner, rr){
        var rp = roundPoly(sil, rr), cxy = [0, 0];
        sil.forEach(function(p){ cxy[0] += p[0]/sil.length; cxy[1] += p[1]/sil.length; });
        var bands = [], bw = Math.max(1, rr * 0.9);
        inner.forEach(function(ed){ bands.push(edgeBand(ed[0], ed[1], bw*0.8, 'rgba(255,255,255,' + (GLOSS ? '.55' : '.32') + ')', true)); });
        for (var i = 0; i < sil.length; i++){
          var p1 = sil[i], p2 = sil[(i + 1) % sil.length];
          var up = (p1[1] + p2[1])/2 < cxy[1] - 1 && Math.abs(p2[1] - p1[1]) < Math.abs(p2[0] - p1[0]) * 1.5;
          bands.push(edgeBand(p1, p2, bw * 1.2, up ? 'rgba(255,255,255,' + (GLOSS ? '.40' : '.24') + ')' : 'rgba(0,0,0,.30)', false, cxy));
        }
        var clip = 'polygon(' + rp.map(function(p){ return r(p[0]) + 'px ' + r(p[1]) + 'px'; }).join(',') + ')';
        var inside = out.splice(from, out.length - from).join('') + bands.join('');
        out.push(node({ left:'0', top:'0', width:px(BW), height:px(BH), 'clip-path':clip, '-webkit-clip-path':clip }, null, inside));
      }

      function poleStack(x, upperY, lowerY, halfH, width){
        var ls = +cfg.letterScale || 1; var sz = Math.min(26*ls, width*0.3*ls, halfH/1.1);   /* te-polarity: letterScale */
        if (sz < 9) return;
        pole(x, upperY, aL, r(sz));
        pole(x, lowerY, bL, r(sz));
      }
      function polePair(options){
        var best = 0, i;
        for (i = 0; i < options.length; i++) best = Math.max(best, options[i].s);
        if (best < 9) return;
        for (i = 0; i < options.length; i++){
          var o = options[i];
          if (o.s >= best * 0.8){ var s = r(o.s); pole(o.a[0], o.a[1], aL, s); pole(o.b[0], o.b[1], bL, s); return; }
        }
      }

      /* ---------- countersink: cone on the top face + through-hole ----------
         Drawn as circles in "face" coordinates, mapped onto the top face with
         matrix(1,0,c,d): c = sideways shear (blocks), d = foreshortening. */
      function countersink(cx, cy, c, d, h, sc, withDims){
        /* True geometry: cone from Ø Dc at the face down to Ø Dh at depth dz, then a
           straight through-hole to the far face at depth h. Everything is drawn in
           "face" coordinates (circles) and mapped onto the top face by matrix(1,0,c,d).
           From the usual viewing angle only the back wall of the cone is visible; the
           front rim hides the rest, so no background shows through unless it really would. */
        var cs = spec.cs, Dc = cs.Dm*sc, Dh = cs.Hm*sc, R = Dc/2, rh = Dh/2;
        var dz = Math.min(h, cs.depth*sc);
        var loc = function(Y){ return [-c*Y/d, Y/d]; };          /* screen-vertical drop -> face coords */
        var skew = r(Math.atan(-c)*180/Math.PI);
        var hc = loc(dz), inner = [];
        var wallBase = axial ? A : split;
        if (axial && h/2 > dz){                                   /* hole wall changes color at mid-thickness */
          var sp = loc(h/2 - dz);
          inner.push(node({ left:px(sp[0]), top:px(sp[1]), width:px(Dh), height:px(Dh), 'border-radius':'50%', background:B }));
          inner.push(node({ left:px(sp[0]), top:px(sp[1] + rh), width:px(Dh), height:px(Dh*4 + h/d), background:B,
                            transform:'skewX(' + skew + 'deg)', 'transform-origin':'50% 0' }));
        } else if (axial) wallBase = B;
        inner.push(node({ left:'0', top:'0', width:'100%', height:'100%',
                          background:'linear-gradient(90deg,rgba(0,0,0,.55),rgba(0,0,0,.72) 35%,rgba(0,0,0,.66) 70%,rgba(0,0,0,.50))' }));
        var bo = loc(h - dz);                                     /* far end of the hole, at the real thickness */
        inner.push(node({ left:px(bo[0]), top:px(bo[1]), width:px(Dh), height:px(Dh), 'border-radius':'50%', background:cfg.bg }));
        var hole = node({ left:px(R + hc[0] - rh), top:px(R + hc[1] - rh), width:px(Dh), height:px(Dh), 'border-radius':'50%',
                          overflow:'hidden', background:wallBase, 'box-shadow':'inset 0 ' + r(1.1/d) + 'px 0 rgba(255,255,255,' + (GLOSS ? '.55' : '.35') + '),inset 0 0 0 ' + r(0.9/d) + 'px rgba(0,0,0,.55),inset 0 ' + r(3/d) + 'px ' + r(4/d) + 'px rgba(0,0,0,.45)' }, null, inner.join(''));

        /* cone wall shading: back wall lit, falling into shadow toward the throat; sides turn away */
        var throat = Math.max(5, Math.min(95, (R + (dz - rh*d)/d) / Dc * 100));
        var CONE = (GLOSS ? 'radial-gradient(ellipse 38% 16% at 44% 12%,rgba(255,255,255,.55),rgba(255,255,255,0) 100%),' : '') +
          'linear-gradient(90deg,rgba(0,0,0,.36) 0%,rgba(0,0,0,.08) 26%,rgba(0,0,0,0) 45%,rgba(0,0,0,.06) 65%,rgba(0,0,0,.40) 100%),' +
          'linear-gradient(180deg,rgba(255,255,255,' + (GLOSS ? '.30' : '.16') + ') 0%,rgba(255,255,255,.05) ' + r(throat*0.45) + '%,rgba(0,0,0,.18) ' + r(throat*0.8) + '%,rgba(0,0,0,.42) ' + r(throat) + '%,rgba(0,0,0,.50) 100%)';
        out.push(node({ left:px(cx - R), top:px(cy - R), width:px(Dc), height:px(Dc), 'border-radius':'50%', overflow:'hidden',
                        background:CONE + ',' + (axial ? A : split), transform:'matrix(1,0,' + r(c*1000)/1000 + ',' + r(d*1000)/1000 + ',0,0)',
                        'transform-origin':'50% 50%',
                        'box-shadow':'inset 0 ' + r(1.5/d) + 'px ' + r(2/d) + 'px rgba(0,0,0,.40),inset 0 -' + r(1/d) + 'px ' + r(3/d) + 'px rgba(0,0,0,.16)' }, null, hole));
        if (cfg.dims && withDims !== false){
          var csTxt = phi + label(cs.rawD, cs.D, cs.unit, cfg.conv, cfg.inch) + ' × ' + trimNum(cs.angle, 1) + '°';
          var hTxt = phi + label(cs.rawH, cs.H, cs.unit, cfg.conv, cfg.inch);
          hDim(cx - rh, cx + rh, -14, cy - 2, hTxt, true);
          hDim(cx - R, cx + R, -14 - TH - 18, cy - 2, csTxt, true);
        }
      }

      /* ---------- cylinder body (disc + ring) ---------- */
      /* Rounded-edge cylinder: the rounded rims are built from stacked ellipses that follow
         a quarter circle of radius rr (a torus cross-section), so the outline and the
         light across the curve both come out right. */
      function cylinderRound(w, h, e, rr){
        var t = e / w, R = w/2;
        rr = Math.min(rr, h/2 * 0.96, R * 0.45);
        var steps = Math.max(3, Math.min(32, Math.ceil(rr / 1.1)));
        var top = axial ? A : split, bot = axial ? B : split;
        var ring = function(rho, D, bg){
          add({ left:px(R - rho), top:px(e/2 + D - rho*t), width:px(2*rho), height:px(2*rho*t), 'border-radius':'50%', background:bg });
        };
        var i, a, rho;
        for (i = 0; i <= steps; i++){                                 /* bottom rounded edge, deepest first */
          a = -Math.PI/2 + (Math.PI/2) * i/steps;
          rho = R - rr + rr*Math.cos(a);
          ring(rho, h - rr - rr*Math.sin(a), SH + ',linear-gradient(rgba(0,0,0,' + r(0.30*(-Math.sin(a))) + '),rgba(0,0,0,' + r(0.30*(-Math.sin(a))) + ')),' + bot);
        }
        if (axial){
          add({ left:'0', top:px(e/2 + h/2), width:px(w), height:px(Math.max(0, h/2 - rr) + 0.5), background:SH + ',' + B });
          add({ left:'0', top:px(h/2), width:px(w), height:px(e), 'border-radius':'50%', background:SH + ',' + A });
          add({ left:'0', top:px(e/2 + rr), width:px(w), height:px(Math.max(0, h/2 - rr) + 0.5), background:SH + ',' + A });
        } else {
          add({ left:'0', top:px(e/2 + rr), width:px(w), height:px(Math.max(0, h - 2*rr) + 0.5), background:SH + ',' + split });
        }
        for (i = 0; i <= steps; i++){                                 /* top rounded edge, lowest first */
          a = (Math.PI/2) * i/steps;
          rho = R - rr + rr*Math.cos(a);
          var side = 1 - Math.sin(a), hi = Math.sin(2*a) * (GLOSS ? 0.42 : 0.24) * Math.max(0.55, 1 - rr/h);
          ring(rho, rr - rr*Math.sin(a),
               'linear-gradient(rgba(255,255,255,' + r(hi) + '),rgba(255,255,255,' + r(hi) + ')),' +
               'linear-gradient(90deg,rgba(0,0,0,' + r(0.30*side) + ') 0%,rgba(255,255,255,' + r(0.2*side) + ') 22%,rgba(0,0,0,0) 50%,rgba(0,0,0,' + r(0.34*side) + ') 100%),' + top);
        }
        var fr = R - rr;                                              /* flat top face */
        add({ left:px(rr), top:px(e/2 - fr*t), width:px(2*fr), height:px(2*fr*t), 'border-radius':'50%', background:HL + ',' + top });
        ext(0, 0, w, h + e);
      }

      function cylinder(w, h, e){
        var rr0 = edgePx();
        if (rr0 >= 0.8) return cylinderRound(w, h, e, rr0);
        if (axial){
          add({ left:'0', top:px(h), width:px(w), height:px(e), 'border-radius':'50%', background:SH + ',' + B });
          add({ left:'0', top:px(h/2 + e/2), width:px(w), height:px(h/2 + 0.5), background:SH + ',' + B });
          add({ left:'0', top:px(h/2), width:px(w), height:px(e), 'border-radius':'50%', background:SH + ',' + A });
          add({ left:'0', top:px(e/2), width:px(w), height:px(h/2 + 0.5), background:SH + ',' + A });
          add({ left:'0', top:'0', width:px(w), height:px(e), 'border-radius':'50%', background:HL + ',' + A, 'box-shadow':RIM });
        } else {
          add({ left:'0', top:px(h), width:px(w), height:px(e), 'border-radius':'50%', background:SH + ',' + split });
          add({ left:'0', top:px(e/2), width:px(w), height:px(h), background:SH + ',' + split });
          add({ left:'0', top:'0', width:px(w), height:px(e), 'border-radius':'50%', background:HL + ',' + split, 'box-shadow':RIM });
        }
        ext(0, 0, w, h + e);
      }

      var w, h, e, gap = 18, alt;
      if (spec.shape === 'disc' || spec.shape === 'ring'){
        sc = maxPx / Math.max(v.d, v.t);
        w = r(v.d*sc); h = r(Math.max(v.t*sc, 2)); e = r(Math.max(w*tilt, 3));
        pf = r(Math.min(26, Math.max(10, Math.min(e, w/2) * 0.5)));
        cylinder(w, h, e);
        if (spec.cs && spec.shape === 'disc') countersink(w/2, e/2, 0, e/w, h, sc);
        var wi = 0, ei = 0;
        if (spec.shape === 'ring'){
          wi = r(v.id*sc); ei = r(wi*tilt);
          var hole = [];
          if (axial){
            hole.push(node({ left:'0', top:'0', width:'100%', height:px(h/2 + ei/2), background:A }));
            hole.push(node({ left:'0', top:px(h/2), width:'100%', height:px(ei), 'border-radius':'50%', background:B }));
          }
          hole.push(node({ left:'0', top:'0', width:'100%', height:'100%', background:'linear-gradient(90deg,rgba(0,0,0,.18),rgba(0,0,0,.42) 30%,rgba(0,0,0,.30) 70%,rgba(0,0,0,.12))' }));
          hole.push(node({ left:'0', top:px(h), width:'100%', height:px(ei), 'border-radius':'50%', background:cfg.bg }));
          out.push(node({ left:px((w-wi)/2), top:px((e-ei)/2), width:px(wi), height:px(ei), 'border-radius':'50%', overflow:'hidden',
                          background:axial ? B : split, 'box-shadow':'inset 0 1px 1px rgba(0,0,0,.25)' }, null, hole.join('')));
        }
        if (cfg.letters){
          if (axial){
            poleStack(w/2, e + h/4, e + 0.75*h, h/2, w);
          } else {
            /* diametric: N and S on the side, one on each half, centered on the side's height */
            var dls = +cfg.letterScale || 1; var dsz = Math.min(26*dls, w*0.3*dls, h/1.15);   /* te-polarity: letterScale */
            if (dsz >= 9){ pole(w*0.27, e + h/2, aL, r(dsz)); pole(w*0.73, e + h/2, bL, r(dsz)); }
          }
        }
        if (cfg.dims){
          hDim(0, w, h + e + 14, h + e/2 + 4, phi + L('d'));
          vDim(w + gap, e/2, e/2 + h, w, L('t'));
          if (spec.shape === 'ring') hDim((w-wi)/2, (w+wi)/2, -14, e/2 - 3, phi + L('id'), true);
        }
        alt = spec.shape === 'disc' ? 'Disc magnet, ' + L('d') + ' diameter by ' + L('t') + ' thick'
                                    : 'Ring magnet, ' + L('d') + ' outer diameter, ' + L('id') + ' inner diameter, ' + L('t') + ' thick';
      }

      else if (spec.shape === 'sphere'){
        w = r(maxPx); e = r(w*tilt);
        pf = r(Math.min(28, Math.max(10, w*0.13)) * (+cfg.letterScale || 1));   /* te-polarity: letterScale */
        var inner = [];
        if (axial){
          inner.push(node({ left:'0', top:'0', width:'100%', height:'50%', background:A }));
          inner.push(node({ left:'0', top:px(w/2 - e/2), width:'100%', height:px(e), 'border-radius':'50%', background:A }));
        }
        inner.push(node({ left:'0', top:'0', width:'100%', height:'100%', background:GLOSS
          ? 'radial-gradient(circle at 33% 27%,rgba(255,255,255,.98) 0,rgba(255,255,255,.85) 3.5%,rgba(255,255,255,0) 13%),radial-gradient(circle at 38% 32%,rgba(255,255,255,.38),rgba(255,255,255,0) 42%),radial-gradient(ellipse 45% 18% at 58% 88%,rgba(255,255,255,.30),rgba(255,255,255,0) 100%),radial-gradient(circle at 45% 40%,rgba(0,0,0,0) 45%,rgba(0,0,0,.55) 100%)'
          : 'radial-gradient(circle at 36% 30%,rgba(255,255,255,.55),rgba(255,255,255,0) 38%),radial-gradient(circle at 50% 50%,rgba(0,0,0,0) 52%,rgba(0,0,0,.38) 100%)' }));
        out.push(node({ left:'0', top:'0', width:px(w), height:px(w), 'border-radius':'50%', overflow:'hidden', background:axial ? B : split }, null, inner.join('')));
        ext(0, 0, w, w);
        if (cfg.letters){
          if (axial){ pole(w/2, w*0.26, aL); pole(w/2, w/2 + e/2 + (w/2 - e/2)/2, bL); }
          else { pole(w*0.28, w/2, aL); pole(w*0.72, w/2, bL); }
        }
        if (cfg.dims) hDim(0, w, w + 14, w/2 + 4, phi + L('d'));
        alt = 'Sphere magnet, ' + L('d') + ' diameter';
      }

      else if (spec.shape === 'block'){
        sc = maxPx / Math.max(v.w, v.l, v.t);
        w = r(v.w*sc); h = r(Math.max(v.t*sc, 2));
        pf = r(Math.min(26, Math.max(10, Math.min(w, h) * 0.3)));
        var FRONT = GLOSS
          ? 'linear-gradient(105deg,rgba(0,0,0,.14) 0%,rgba(255,255,255,.08) 18%,rgba(255,255,255,.62) 27%,rgba(255,255,255,.12) 33%,rgba(255,255,255,0) 48%,rgba(255,255,255,.20) 70%,rgba(255,255,255,0) 78%,rgba(0,0,0,.20) 100%)'
          : 'linear-gradient(90deg,rgba(0,0,0,.06),rgba(255,255,255,.14) 35%,rgba(255,255,255,.04) 65%,rgba(0,0,0,.10))';
        var TOP = GLOSS
          ? 'linear-gradient(100deg,rgba(255,255,255,.55) 0%,rgba(255,255,255,.16) 30%,rgba(255,255,255,.34) 55%,rgba(255,255,255,.06) 75%,rgba(255,255,255,.18) 100%)'
          : 'linear-gradient(90deg,rgba(255,255,255,.22),rgba(255,255,255,.08))';
        var poly = function(pts){ return 'polygon(' + pts.map(function(p){ return r(p[0]) + 'px ' + r(p[1]) + 'px'; }).join(',') + ')'; };
        var mid = h/2;

        if (spec.cs){
          /* Countersunk block: true 3D view (block turned about its vertical axis and seen
             from above), so round features on the top face appear as level ovals and the
             countersink + through-hole use exactly the same geometry as on discs. */
          var turn = (+cfg.csBlockTurn || 24) * Math.PI/180, ct = Math.cos(turn), st = Math.sin(turn);
          var Lp = v.l*sc;
          var P0 = function(X, Y, Z){ return [X*ct + Z*st, -Y - (Z*ct - X*st)*tilt]; };
          var mnx = Infinity, mny = Infinity, mxx = -Infinity, mxy = -Infinity;
          [0, w].forEach(function(X){ [0, h].forEach(function(Y){ [0, Lp].forEach(function(Z){
            var q = P0(X, Y, Z); mnx = Math.min(mnx, q[0]); mny = Math.min(mny, q[1]); mxx = Math.max(mxx, q[0]); mxy = Math.max(mxy, q[1]);
          }); }); });
          var P = function(X, Y, Z){ var q = P0(X, Y, Z); return [q[0] - mnx, q[1] - mny]; };
          var BW = mxx - mnx, BH = mxy - mny;
          var faceO = function(bg, pts){ add({ left:'0', top:'0', width:px(BW), height:px(BH), background:bg, 'clip-path':poly(pts), '-webkit-clip-path':poly(pts) }); };
          var SIDEO = 'linear-gradient(90deg,rgba(0,0,0,.26),rgba(0,0,0,.40))';
          var startO = out.length, rrO = Math.min(edgePx(), h*0.45, w*0.3, Lp*0.3);
          faceO(TOP + ',' + A, [P(0,h,0), P(w,h,0), P(w,h,Lp), P(0,h,Lp)]);
          faceO(FRONT + ',' + A, [P(0,h,0), P(w,h,0), P(w,mid,0), P(0,mid,0)]);
          faceO(FRONT + ',' + B, [P(0,mid,0), P(w,mid,0), P(w,0,0), P(0,0,0)]);
          faceO(SIDEO + ',' + A, [P(w,h,0), P(w,h,Lp), P(w,mid,Lp), P(w,mid,0)]);
          faceO(SIDEO + ',' + B, [P(w,mid,0), P(w,mid,Lp), P(w,0,Lp), P(w,0,0)]);
          var e1 = P(0,h,0), e2 = P(w,h,0), e3 = P(w,0,0), hl = GLOSS ? 'rgba(255,255,255,.75)' : 'rgba(255,255,255,.35)';
          if (rrO >= 0.8){
            roundBlock(startO, BW, BH, [P(0,0,0), P(w,0,0), P(w,0,Lp), P(w,h,Lp), P(0,h,Lp), P(0,h,0)],
                       [[P(0,h,0), P(w,h,0)], [P(w,h,0), P(w,h,Lp)], [P(w,h,0), P(w,0,0)]], rrO);
          } else {
            faceO(hl, [e1, e2, [e2[0], e2[1] + 1.2], [e1[0], e1[1] + 1.2]]);                 /* front top edge */
            faceO(GLOSS ? 'rgba(255,255,255,.45)' : 'rgba(255,255,255,.25)', [e2, [e2[0] + 1.2, e2[1]], [e3[0] + 1.2, e3[1]], e3]);   /* front right edge */
          }
          ext(0, 0, BW, BH);
          /* one hole in the middle, or two holes spaced along the block's longer side */
          var holes = [[w/2, Lp/2]], sp2 = spec.cs.Sm ? spec.cs.Sm*sc : 0;
          if (sp2 > 0) holes = w >= Lp ? [[w/2 - sp2/2, Lp/2], [w/2 + sp2/2, Lp/2]] : [[w/2, Lp/2 + sp2/2], [w/2, Lp/2 - sp2/2]];
          holes.forEach(function(hp, i){ var q = P(hp[0], h, hp[1]); countersink(q[0], q[1], 0, tilt, h, sc, i === 0); });
          if (holes.length === 2 && cfg.dims){
            var q1 = P(holes[0][0], h, holes[0][1]), q2 = P(holes[1][0], h, holes[1][1]), spc = spec.cs.spacing;
            var apTxt = label(spc.raw, spc.v, spc.unit, cfg.conv, cfg.inch) + ' apart';
            if (w >= Lp)                                             /* holes side by side: dimension above */
              hDim(Math.min(q1[0], q2[0]), Math.max(q1[0], q2[0]), -14 - 2*(TH + 18), Math.min(q1[1], q2[1]) - 2, apTxt, true);
            else                                                     /* holes front-to-back: dimension along them, beside the block */
              sDim(q2[0], q2[1], q1[0], q1[1], apTxt, -(w*ct/2 + 30));
          }
          if (cfg.letters){
            var pu = P(w/2, h*0.75, 0), pl = P(w/2, h*0.25, 0);
            poleStack(pu[0], pu[1], pl[1], mid, w*ct);
          }
          if (cfg.dims){
            var a0 = P(0,0,0), a1 = P(w,0,0), a2 = P(w,0,Lp), tt = P(w,h,Lp);
            sDim(a0[0], a0[1], a1[0], a1[1], L('w'), 16);
            sDim(a1[0], a1[1], a2[0], a2[1], L('l'), 16);
            vDim(a2[0] + gap, tt[1], a2[1], a2[0], L('t'));
          }
        } else {
          var kf = Math.min(0.75, tilt*1.6);
          var dep = v.l*sc*kf;
          var dx = r(dep*Math.cos(Math.PI/6)), dy = r(dep*Math.sin(Math.PI/6));
          var W = w + dx, H = dy + h;
          var face = function(bg, pts){ add({ left:'0', top:'0', width:px(W), height:px(H), background:bg, 'clip-path':poly(pts), '-webkit-clip-path':poly(pts) }); };
          var SIDE = GLOSS
            ? 'linear-gradient(90deg,rgba(255,255,255,.28) 0%,rgba(0,0,0,.30) 12%,rgba(0,0,0,.42) 100%)'
            : 'linear-gradient(rgba(0,0,0,.30),rgba(0,0,0,.30))';
          var startB = out.length, rrB = Math.min(edgePx(), h*0.45, w*0.3, Math.max(dy, 1)*0.9);
          face(TOP + ',' + A, [[0,dy],[w,dy],[W,0],[dx,0]]);
          face(FRONT + ',' + A, [[0,dy],[w,dy],[w,dy+mid],[0,dy+mid]]);
          face(FRONT + ',' + B, [[0,dy+mid],[w,dy+mid],[w,H],[0,H]]);
          face(SIDE + ',' + A, [[w,dy],[W,0],[W,mid],[w,dy+mid]]);
          face(SIDE + ',' + B, [[w,dy+mid],[W,mid],[W,h],[w,H]]);
          if (rrB >= 0.8){
            roundBlock(startB, W, H, [[0,H],[w,H],[W,h],[W,0],[dx,0],[0,dy]],
                       [[[0,dy],[w,dy]], [[w,dy],[W,0]], [[w,dy],[w,H]]], rrB);
          } else {
            add({ left:'0', top:px(dy), width:px(w), height:'1px', background:GLOSS ? 'rgba(255,255,255,.75)' : 'rgba(255,255,255,.35)' });
          }
          ext(0, 0, W, H);
          if (cfg.letters) poleStack(w/2, dy + mid/2, dy + mid*1.5, mid, w);
          if (cfg.dims){
            hDim(0, w, H + 14, H + 4, L('w'));
            vDim(W + gap, 0, h, W, L('t'));
            if (dx > 4) sDim(w, H, W, h, L('l'), 16);
          }
        }
        alt = 'Block magnet, ' + L('w') + ' wide by ' + L('l') + ' long by ' + L('t') + ' thick';
      }

      if (spec.cs && spec.cs.Sm && spec.shape === 'block') alt += ', two countersunk holes ' + trimNum(spec.cs.spacing.v, 3) + ' ' + spec.cs.spacing.unit + ' apart';
      if (spec.cs) alt += ', countersunk ' + trimNum(spec.cs.D, 3) + ' by ' + trimNum(spec.cs.H, 3) + ' ' + spec.cs.unit + ' at ' + trimNum(spec.cs.angle, 1) + ' degrees';
      if (cfg.caption){
        var cxm = (bx.x1 + bx.x2) / 2, cyTop = bx.y2 + 10, cw = textW(cfg.caption);
        out.push(node({ left:px(cxm), top:px(cyTop), transform:'translateX(-50%)', 'white-space':'nowrap', 'line-height':'1.2', 'font-weight':'600' }, cfg.caption));
        ext(cxm - cw/2, cyTop, cxm + cw/2, cyTop + TH);
        alt += ', ' + cfg.caption.toLowerCase();
      }
      /* glossy highlights: every white highlight is rgba(255,255,255,a); scale a by the
         intensity and swap in the highlight color */
      var body = out.join('\n');
      if (GLOSS){
        var gk = parseFloat(cfg.glossIntensity); if (!(gk >= 0)) gk = 1;
        var gc = hexRgb(cfg.glossColor) || [255, 255, 255];
        if (gk !== 1 || gc.join(',') !== '255,255,255'){
          body = body.replace(/rgba\(255,255,255,\s*([\d.]+)\)/g, function(m, a){
            return 'rgba(' + gc.join(',') + ',' + r(Math.min(1, parseFloat(a) * gk)) + ')';
          });
        }
      }
      var ox = r(-bx.x1 + 1), oy = r(-bx.y1 + 1);
      var width = Math.ceil(bx.x2 - bx.x1 + 2), height = Math.ceil(bx.y2 - bx.y1 + 2);
      var html = '<div role="img" aria-label="' + esc(alt + ', ' + where) + '" style="' +
        esc('position:relative;display:inline-block;vertical-align:top;text-align:left;width:' + width + 'px;height:' + height + 'px;font-family:inherit;font-size:' + fs + 'px;line-height:1.2;color:' + c) +
        '">\n<div style="position:absolute;left:' + ox + 'px;top:' + oy + 'px">\n' + body + '\n</div>\n</div>';
      return { html:html, width:width, height:height };
    }

    return { parseLen:parseLen, parseSize:parseSize, parseCs:parseCs, addCs:addCs, csVariants:csVariants, parseDir:parseDir, parseNum:parseNum, build:build, label:label, DEFAULTS:DEFAULTS };
  })();
  /* ================================================================== END DRAWING ENGINE */

  /* ------------------------------------------------------------------ drawing */
  // Keep thin discs, long rods and thin plates readable: nothing beyond maxRatio : 1
  function clampRatio(spec) {
    var v = spec.v, R = CFG.maxRatio, k;
    if (spec.shape === 'disc' || spec.shape === 'ring') {
      if (v.t < v.d / R) v.t = v.d / R;
      if (v.d < v.t / R) { k = (v.t / R) / v.d; v.d *= k; if (v.id) v.id *= k; }
    } else if (spec.shape === 'block') {
      var m = Math.max(v.w, v.l, v.t);
      ['w', 'l', 't'].forEach(function (key) { if (v[key] < m / R) v[key] = m / R; });
    }
    return spec;
  }

  // product data -> { html, W, H, parts } or null
  function drawingFor(p, onProductPage) {
    if (!p || !p.m) return null;
    var size = p.s || p.d;
    if (!size) return null;
    var spec = TeMag.parseSize(size, p.m, 'in');
    if (spec.error && p.d && size !== p.d) spec = TeMag.parseSize(p.d, p.m, 'in');
    if (spec.error) {
      if (w.console) console.warn('[te-polarity] skipped (' + spec.error + ') Size: "' + size + '", direction: "' + p.m + '"');
      return null;
    }
    TeMag.addCs(spec, p.c);
    var variants = TeMag.csVariants(spec, p.m);
    clampRatio(spec);
    var parts = variants.map(function (v) {
      var o = {}, k;
      for (k in CFG.drawing) o[k] = CFG.drawing[k];
      for (k in v) o[k] = v[k];
      o.max = CFG.renderSize; o.dims = false; o.caption = '';
      if (!onProductPage && !CFG.cardLetters) o.letters = false;   // no N and S on cards
      return TeMag.build(spec, o);
    });
    // pairs sit side by side; wide, flat ones stack (like the product page's row wrapping on a phone)
    var stack = parts.length > 1 && parts[0].width / parts[0].height > 1.6;
    var gap = parts.length > 1 ? (stack ? Math.round(CFG.pairGap * 0.6) : CFG.pairGap) : 0, W = 0, H = 0;
    parts.forEach(function (q) {
      if (stack) { W = Math.max(W, q.width); H += q.height; } else { W += q.width; H = Math.max(H, q.height); }
    });
    if (stack) H += gap * (parts.length - 1); else W += gap * (parts.length - 1);
    var html = '<div style="display:flex;' + (stack ? 'flex-direction:column;align-items:flex-start' : 'align-items:flex-end') +
      ';gap:' + gap + 'px;width:' + W + 'px;height:' + H + 'px">' + parts.map(function (q) { return q.html; }).join('') + '</div>';
    return { html: html, W: W, H: H, parts: parts };
  }

  // Badge width as a multiple of --te-pol-size: flat magnets wider, rods narrower (height
  // capped at about 1.25x the size), pairs wider still. Read by te-polarity.css as --te-pol-w.
  function widthMult(dr) {
    var u = dr.parts[0], unitAr = u.width / u.height + 0.15;
    var m = Math.min(Math.max(0.72, Math.sqrt(unitAr) * 1.05), 1.6, unitAr * 1.25) * (dr.W / u.width);
    return Math.min(m, 2.2);
  }

  /* ------------------------------------------------------------------ pull force */
  // The store's weight icon (the "LB" weight from totalelement-pull-force-init.js), drawn in the text color
  var PF_ICON = '<svg class="te-pol-pf-i" viewBox="0 0 512 512" aria-hidden="true" focusable="false"><g fill="currentColor">' +
    '<path d="M346.516,308.563c0-6.469-4.828-12.563-23.469-12.563H304v28h19.047C343.938,324,346.516,315.016,346.516,308.563z"/>' +
    '<path d="M479.703,446.234l-39.547-276.859C438.078,154.813,425.594,144,410.875,144h-81.656c4.313-9.828,6.781-20.625,6.781-32' +
    'c0-44.125-35.891-80-80-80c-44.109,0-80,35.875-80,80c0,11.375,2.469,22.172,6.781,32h-81.656c-14.719,0-27.188,10.813-29.281,25.375' +
    'L32.297,446.234c-1.203,8.483,1.328,17.078,6.938,23.563C44.845,476.282,53,480,61.578,480h388.844c8.578,0,16.734-3.719,22.344-10.203' +
    'C478.391,463.313,480.922,454.719,479.703,446.234z M240,395c0,2.75-2.25,5-5,5h-75h-27c-2.75,0-5-2.25-5-5V277c0-2.766,2.25-5,5-5h22' +
    'c2.75,0,5,2.234,5,5v91h75c2.75,0,5,2.234,5,5V395z M223.203,112c0-18.125,14.672-32.813,32.797-32.813c18.109,0,32.797,14.688,32.797,32.813' +
    'c0,18.109-14.688,32.781-32.797,32.781C237.875,144.781,223.203,130.109,223.203,112z M336.531,400H277c-2.766,0-5-2.25-5-5V277' +
    'c0-2.766,2.234-5,5-5h59.141c33.859,0,41.547,20.438,41.547,30.75c0,3.188-0.469,6.313-1.405,9.344c-0.922,3.031-2.375,5.828-4.313,8.375' +
    'c-1.953,2.547-4.391,4.797-7.313,6.75c-2.922,1.969-6.375,3.5-10.344,4.609c3.969,0.516,7.766,1.547,11.375,3.078c3.625,1.531,6.78,3.531,9.5,5.984' +
    'c2.719,2.452,4.875,5.327,6.452,8.672c1.578,3.313,2.375,7.047,2.375,11.155C384.016,373.781,376,400,336.531,400z"/>' +
    '<path d="M327.828,348H304v27.984h23.828c20.891,0,23.469-8.892,23.469-15.438S346.469,348,327.828,348z"/></g></svg>';

  function esc(s) { return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;'); }

  // Pull Force metafield -> { num, text, icon }. "8.15 lbs", "530lbs" and a bare "65.7" are
  // pounds and get the weight icon, like the old tag. Anything else ("5.7 lbs/in²", "12 kg")
  // is shown as written.
  function pullForce(raw) {
    var s = String(raw == null ? '' : raw).replace(/\s+/g, ' ').trim();
    var m = /(\d[\d,]*(?:\.\d+)?)\s*(.*)$/.exec(s);
    if (!m) return null;
    var v = parseFloat(m[1].replace(/,/g, ''));
    if (!(v > 0)) return null;
    var num = v.toLocaleString('en-US', { maximumFractionDigits: 2 });
    var rest = m[2].trim(), unit = /^(lbs?|pounds?)\b\.?/i.exec(rest);
    var extra = unit ? rest.slice(unit[0].length).trim() : '';
    if (!rest || (unit && !extra)) return { num: num, text: num + ' lbs', icon: true };
    var text = num + ' ' + (unit ? 'lbs' + (/^[\/²³]/.test(extra) ? '' : ' ') + extra : rest);
    return { num: num, text: text, icon: false };
  }

  function pfTag(pf, onProductPage) {
    var url = onProductPage && CFG.pullForceUrl;
    var el = d.createElement(url ? 'a' : 'span');
    el.className = 'te-pol-pf' + (url ? ' te-pol--link' : '');
    if (url) el.href = url;
    el.title = 'Pull force: ' + pf.text;
    // Product page: written out like the theme's old tag, weight icon then "7.9 lbs Pull Force".
    // A value that is only a number ("65.7") gets "lbs" added: pf.text is already "65.7 lbs".
    // The icon shows for any value in pounds (including "lbs/in²").
    if (onProductPage) {
      el.innerHTML = (pf.icon || /\blbs\b/.test(pf.text) ? PF_ICON : '') +
        '<span class="te-pol-pf-n">' + esc(pf.text) + '</span>' +
        (CFG.pullForceLabel ? '<span class="te-pol-pf-l">' + esc(CFG.pullForceLabel) + '</span>' : '');
      return el;
    }
    var label = '<span class="te-pol-sr">Pull force </span>';
    el.innerHTML = label + (pf.icon
      ? '<span class="te-pol-pf-n">' + esc(pf.num) + '</span>' + PF_ICON + '<span class="te-pol-sr"> lbs</span>'
      : '<span class="te-pol-pf-n">' + esc(pf.text) + '</span>');
    return el;
  }

  // Grade metafield -> tag text ("N52", "N42SH", "C5"); blank -> null
  function gradeText(raw) {
    var s = String(raw == null ? '' : raw).replace(/\s+/g, ' ').trim();
    return s ? s.toUpperCase() : null;
  }
  function gradeTag(gr, onProductPage) {
    var el = d.createElement('span');
    el.className = 'te-pol-gr';
    el.title = 'Grade: ' + gr;
    el.innerHTML = (onProductPage && CFG.gradeLabel
      ? '<span class="te-pol-pf-l">' + esc(CFG.gradeLabel) + '</span>'
      : '<span class="te-pol-sr">Grade </span>') + '<span class="te-pol-pf-n">' + esc(gr) + '</span>';
    return el;
  }

  /* ------------------------------------------------------------------ badge */
  // Lay out one badge for its image's current width: tag text size, then the magnet
  // drawing scaled to its box. Runs again whenever the image changes size.
  function fit(g) {
    var gw = g.clientWidth;
    if (!(gw > 0)) return;                       // hidden right now: fits again when shown
    var f = CFG.fontPx;
    g.style.fontSize = Math.max(f[0], Math.min(f[1], gw * 0.055 + 1)).toFixed(1) + 'px';
    if (g.classList.contains('te-pol--pp')) clearSaleTag(g);
    var el = g.querySelector('.te-pol-mag'), inner = el && el.firstChild, info = el && el._tePol;
    if (!inner || !info) return;
    var cs = w.getComputedStyle(el);
    var padX = parseFloat(cs.paddingLeft) + parseFloat(cs.paddingRight);
    var padY = parseFloat(cs.paddingTop) + parseFloat(cs.paddingBottom);
    var brd = parseFloat(cs.borderTopWidth) + parseFloat(cs.borderBottomWidth);
    var cw = el.clientWidth - padX;
    if (!(cw > 0)) return;
    var k = cw / info.W;
    inner.style.transform = 'scale(' + k.toFixed(4) + ')';
    el.style.height = (info.H * k + padY + brd).toFixed(1) + 'px';
  }
  // Product page: the theme's script adds a SALE tag to the photo (top-left today). If it
  // would cover the tags, move the tags down just below it.
  function clearSaleTag(g) {
    var tags = g.querySelector('.te-pol-tags'), host = g.parentElement, sale = null;
    if (!tags || !host) return;
    tags.style.marginTop = '';
    for (var i = 0; i < host.children.length; i++) {
      if (host.children[i].classList.contains('sale-tag')) { sale = host.children[i]; break; }
    }
    if (!sale || !sale.offsetWidth) return;
    var s = sale.getBoundingClientRect(), t = tags.getBoundingClientRect();
    if (s.right > t.left && s.left < t.right && s.bottom > t.top && s.top < t.bottom)
      tags.style.marginTop = Math.ceil(s.bottom - t.top + 6) + 'px';
  }
  // Watch the image, not the badge: the badge never changes the image's size
  var ro = w.ResizeObserver ? new w.ResizeObserver(function (entries) {
    entries.forEach(function (en) {
      var host = en.target;
      if (!host.isConnected) { ro.unobserve(host); return; }
      if (host._tePolGroup) fit(host._tePolGroup);
    });
  }) : null;
  if (!ro) w.addEventListener('resize', function () {
    var all = d.querySelectorAll('.te-pol');
    for (var i = 0; i < all.length; i++) fit(all[i]);
  });

  // Public: product data -> badge element, or null when there is nothing to show.
  // onProductPage: the magnet links to the FAQ and the tags read "Grade N52", "7.9 lbs Pull Force".
  function badge(p, onProductPage) {
    if (!p) return null;
    var sp = CFG.polarity;
    var showDr = sp === true || (sp === 'cards' && !onProductPage) || (sp === 'product' && !!onProductPage);
    var dr = showDr ? drawingFor(p, onProductPage) : null;
    var pf = CFG.pullForce ? pullForce(p.p) : null;
    var gr = CFG.grade ? gradeText(p.g) : null;
    if (!dr && !pf && !gr) return null;
    var g = d.createElement('span');
    // te-pol--card: product cards (magnet bottom-left, tags stacked bottom-right)
    // te-pol--pp:   product page (tags top-left)
    g.className = 'te-pol ' + (onProductPage ? 'te-pol--pp' : 'te-pol--card');
    if (gr || pf) {
      var tags = d.createElement('span');
      tags.className = 'te-pol-tags';
      if (gr) tags.appendChild(gradeTag(gr, onProductPage));
      if (pf) tags.appendChild(pfTag(pf, onProductPage));
      g.appendChild(tags);
    }
    if (dr) {
      var url = onProductPage && CFG.linkOnProductPage && CFG.learnUrl;
      var el = d.createElement(url ? 'a' : 'span');
      el.className = 'te-pol-mag' + (dr.parts.length > 1 ? ' te-pol--pair' : '') + (url ? ' te-pol--link' : '');
      el.style.setProperty('--te-pol-w', widthMult(dr).toFixed(3));
      el.title = 'Magnetization: ' + p.m + (dr.parts.length > 1 ? ' (countersunk on the north side and on the south side)' : '');
      if (url) el.href = url;
      el.innerHTML = '<span class="te-pol-in" style="width:' + dr.W + 'px;height:' + dr.H + 'px">' + dr.html + '</span>';
      el._tePol = dr;
      g.appendChild(el);
    }
    g._tePolPf = !!pf;
    return g;
  }

  function attach(host, p, onProductPage) {
    var g = badge(p, onProductPage);
    if (!g) return null;
    if (w.getComputedStyle(host).position === 'static') host.classList.add('te-pol-host');
    host.appendChild(g);
    host._tePolGroup = g;
    fit(g);
    if (ro) ro.observe(host);
    return g;
  }

  /* ------------------------------------------------------------------ data */
  var mapPromise = null;
  function readCache() {
    try {
      var c = JSON.parse(w.localStorage.getItem(CFG.cacheKey) || 'null');
      if (c && c.t && Date.now() - c.t < CFG.cacheHours * 3600e3) return c.m;
    } catch (e) { /* storage blocked */ }
    return null;
  }
  function writeCache(m) {
    try { w.localStorage.setItem(CFG.cacheKey, JSON.stringify({ t: Date.now(), m: m })); } catch (e) { /* full or blocked */ }
  }
  function fetchPage(n) {
    var url = CFG.dataUrl + (CFG.dataUrl.indexOf('?') > -1 ? '&' : '?') + 'page=' + n;
    return fetch(url, { credentials: 'same-origin' }).then(function (r) {
      if (!r.ok) throw new Error('te-polarity data ' + r.status);
      return r.text();
    }).then(function (t) { return JSON.parse(t); });
  }
  function loadMap() {
    if (mapPromise) return mapPromise;
    if (CFG.data) return (mapPromise = Promise.resolve(CFG.data));
    var cached = readCache();
    if (cached) return (mapPromise = Promise.resolve(cached));
    mapPromise = fetchPage(1).then(function (first) {
      var map = first.products || {}, rest = [];
      for (var i = 2; i <= (first.pages || 1); i++) rest.push(fetchPage(i));
      return Promise.all(rest).then(function (pages) {
        pages.forEach(function (pg) { Object.keys(pg.products || {}).forEach(function (k) { map[k] = pg.products[k]; }); });
        writeCache(map);
        return map;
      });
    }).catch(function (e) {
      if (w.console) console.warn('[te-polarity]', e);
      return {};
    });
    return mapPromise;
  }

  /* ------------------------------------------------------------------ page wiring */
  function handleFrom(href) {
    var m = /\/products\/([^\/?#]+)/.exec(href || '');
    try { return m ? decodeURIComponent(m[1]).toLowerCase() : null; } catch (e) { return null; }
  }

  // The product card around a link: the largest ancestor whose product links all go to
  // this product. The old pull-force tag lives somewhere inside it.
  function cardRoot(a, h) {
    var el = a;
    for (var i = 0; i < 6; i++) {
      var up = el.parentElement;
      if (!up || up === d.body || up === d.documentElement) break;
      var links = up.querySelectorAll(CFG.cardLinks), other = false;
      for (var j = 0; j < links.length && !other; j++) other = handleFrom(links[j].getAttribute('href')) !== h;
      if (other) break;
      el = up;
    }
    return el;
  }
  function hideOldPullForce(scope) {
    if (CFG.hideOldPullForce && scope && scope.classList) scope.classList.add('te-pol-pf-on');
  }

  function runProductPage() {
    var tags = d.querySelectorAll('script[data-te-polarity="product"]');
    for (var i = 0; i < tags.length; i++) {
      var p;
      try { p = JSON.parse(tags[i].textContent); } catch (e) { continue; }
      var scope = tags[i].closest('#ProductSection, [data-section-type="product-template"]') || d.body;
      var hosts = scope.querySelectorAll(CFG.galleryImages);
      for (var j = 0; j < hosts.length; j++) {
        if (hosts[j].hasAttribute('data-te-pol')) continue;
        hosts[j].setAttribute('data-te-pol', '1');
        var g = attach(hosts[j], p, true);
        if (g && g._tePolPf) hideOldPullForce(scope);
      }
    }
  }

  function runCards() {
    var links = d.querySelectorAll(CFG.cardLinks), todo = [];
    for (var i = 0; i < links.length; i++) {
      var a = links[i];
      if (a.closest(CFG.exclude)) continue;
      var img = a.querySelector('img');
      if (!img) continue;
      var host = img.closest(CFG.imageHosts) || img.parentElement;
      if (!host || !a.contains(host) || host.hasAttribute('data-te-pol')) continue;
      var wdt = host.getBoundingClientRect().width;
      if (wdt && wdt < CFG.minImageWidth) { host.setAttribute('data-te-pol', '0'); continue; }
      var h = handleFrom(a.getAttribute('href'));
      if (!h) continue;
      host.setAttribute('data-te-pol', 'pending');
      todo.push([host, h, a]);
    }
    if (!todo.length) return;
    loadMap().then(function (map) {
      todo.forEach(function (x) {
        var g = map[x[1]] ? attach(x[0], map[x[1]], false) : null;
        x[0].setAttribute('data-te-pol', g ? '1' : '0');
        if (g && g._tePolPf) hideOldPullForce(cardRoot(x[2], x[1]));
      });
    });
  }

  var queued = false;
  function run() {
    queued = false; runProductPage(); runCards();
    // re-check product-page badges, e.g. after the theme adds its SALE tag
    var pp = d.querySelectorAll('.te-pol--pp');
    for (var i = 0; i < pp.length; i++) fit(pp[i]);
  }
  function schedule() { if (!queued) { queued = true; setTimeout(run, 120); } }

  function start() {
    run();
    if (w.MutationObserver) {
      new MutationObserver(function (muts) {
        for (var i = 0; i < muts.length; i++) if (muts[i].addedNodes.length) { schedule(); return; }
      }).observe(d.body, { childList: true, subtree: true });
    }
  }

  w.TEPolarity = { badge: badge, attach: attach, drawing: drawingFor, pullForce: pullForce, grade: gradeText, refresh: schedule, config: CFG, engine: TeMag,
    clearCache: function () { try { w.localStorage.removeItem(CFG.cacheKey); } catch (e) {} mapPromise = null; } };

  if (d.readyState === 'loading') d.addEventListener('DOMContentLoaded', start); else start();
})(window, document);
